import React, { useEffect } from 'react';
import { CardList } from 'react-bootstrap-icons';
import {
  formatOcCodeLabel,
  getSelectionIndicationRows,
} from '../../utils/ocRequest';
import { logInfo } from '../../config_and_helpers/logger';
import './OcRequestStyle.css';

const LOG_CONTEXT = 'OcSelectionRequirementsPanel';

const display = value => {
  if (value == null || String(value).trim() === '') return '—';
  return String(value).trim();
};

const formatDate = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString();
};

const isFilled = value => value != null && String(value).trim() !== '';

/**
 * Prominent client matching criteria for data review and technical review.
 * Distinct from the Detail "Request & criteria" card so reviewers see it in-place.
 *
 * @param {Object} props
 * @param {Object} [props.request] - OC request detail payload
 */
const OcSelectionRequirementsPanel = ({ request }) => {
  const data = request || {};
  const criteria = data.criteria || {};
  const { rows: indicationRows, notes: quantityNotes } =
    getSelectionIndicationRows(criteria);

  useEffect(() => {
    if (!data.request_number) return;
    logInfo(LOG_CONTEXT, 'selection requirements shown', {
      requestNumber: data.request_number,
      indicationCount: indicationRows.length,
      hasMandatory: isFilled(criteria.mandatory_requirements),
      hasSpecificOcs: isFilled(criteria.specific_oc_numbers),
    });
  }, [
    data.request_number,
    indicationRows.length,
    criteria.mandatory_requirements,
    criteria.specific_oc_numbers,
  ]);

  const otherChips = [
    { label: 'KRAS', value: formatOcCodeLabel(criteria.kras_status) },
    { label: 'Format', value: formatOcCodeLabel(criteria.preferred_format) },
    {
      label: 'Specific OCs',
      value: display(criteria.specific_oc_numbers),
      alert: isFilled(criteria.specific_oc_numbers),
    },
  ];

  const details = [
    { label: 'Quantity notes', value: quantityNotes },
    { label: 'Passage', value: criteria.passage_requirements },
    { label: 'Required medium', value: criteria.required_medium },
    { label: 'Other molecular', value: criteria.other_molecular },
    { label: 'Treatment history', value: criteria.treatment_history },
    { label: 'Research purpose', value: criteria.research_purpose },
    { label: 'Preferred extras', value: criteria.preferred_requirements },
  ].filter(item => isFilled(item.value));

  const delivery = formatDate(data.requested_delivery_date);
  const contextBits = [
    data.organization,
    data.project_name,
    delivery ? `Delivery ${delivery}` : '',
  ].filter(isFilled);

  return (
    <section
      className="oc-selection-panel mb-3"
      aria-labelledby="oc-selection-heading"
    >
      <div className="oc-selection-panel__header">
        <span className="oc-selection-panel__icon" aria-hidden>
          <CardList size={22} />
        </span>
        <div>
          <h3 id="oc-selection-heading" className="oc-selection-panel__title">
            Selection requirements
          </h3>
          <p className="oc-selection-panel__lede">
            Match and review OCs against these client criteria — you do not need
            to leave this tab.
          </p>
        </div>
      </div>

      <div className="oc-selection-indications">
        <div className="oc-selection-indications__label">Requested cultures</div>
        <div className="oc-selection-panel__chips oc-selection-panel__chips--indications">
          {indicationRows.length ? (
            indicationRows.map(row => (
              <div
                key={row.code}
                className="oc-selection-chip oc-selection-chip--indication"
              >
                <span className="oc-selection-chip__label">{row.label}</span>
                <span className="oc-selection-chip__value">
                  {row.vials != null ? (
                    <>
                      {row.vials}
                      <span className="oc-selection-chip__unit">
                        {row.vials === 1 ? ' vial' : ' vials'}
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
            ))
          ) : (
            <div className="oc-selection-chip oc-selection-chip--indication">
              <span className="oc-selection-chip__label">Indication</span>
              <span className="oc-selection-chip__value">—</span>
            </div>
          )}
        </div>
      </div>

      <div className="oc-selection-panel__chips">
        {otherChips.map(chip => (
          <div
            key={chip.label}
            className={`oc-selection-chip${
              chip.alert ? ' oc-selection-chip--alert' : ''
            }`}
          >
            <span className="oc-selection-chip__label">{chip.label}</span>
            <span className="oc-selection-chip__value">{chip.value}</span>
          </div>
        ))}
      </div>

      {isFilled(criteria.mandatory_requirements) ? (
        <div className="oc-selection-must" role="note">
          <strong>Must have:</strong> {criteria.mandatory_requirements}
        </div>
      ) : null}

      {isFilled(criteria.special_requests) ? (
        <div className="oc-selection-special" role="note">
          <strong>Special requests:</strong> {criteria.special_requests}
        </div>
      ) : null}

      {details.length ? (
        <dl className="oc-selection-panel__grid">
          {details.map(item => (
            <div key={item.label} className="oc-selection-detail">
              <dt>{item.label}</dt>
              <dd>{display(item.value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {contextBits.length ? (
        <p className="oc-selection-panel__context mb-0">{contextBits.join(' · ')}</p>
      ) : null}
    </section>
  );
};

export default OcSelectionRequirementsPanel;
